<template>
  <div class="video-player-wrapper">
    <!-- Заглушка: відсутній/некоректний src -->
    <div v-if="!videoSrc" class="video-alert">
      <div class="alert alert-warning">
        <i class="fa fa-link-off"></i>
        <p>The video source is missing or incorrect. Please contact support or try again later.</p>
      </div>
    </div>

    <!-- Заглушка: обробка відео -->
    <div v-else-if="isProcessing && !hasLoadError" class="video-alert">
      <div class="alert alert-info">
        <i class="fa fa-clock"></i>
        <p>The video is being prepared for playback. Please wait or refresh the page a little later.</p>
      </div>
    </div>

    <!-- Заглушка: помилка завантаження -->
    <div v-else-if="hasLoadError" class="video-alert">
      <div class="alert alert-error">
        <i class="fa fa-video-slash"></i>
        <p>Failed to load the video. It may still be processing, or the link is unavailable.</p>
        <button @click="retryVideo" class="retry-button">Try Again</button>
      </div>
    </div>

    <!-- VIDEO PLAYER -->
    <video
      v-if="!isProcessing && !hasLoadError && videoSrc"
      ref="videoPlayer"
      class="video-js vjs-default-skin vjs-big-play-centered"
      :id="playerId"
    ></video>
  </div>
</template>

<script>
import { sendData } from "../../../global-declare-components/api/network";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-hls-quality-selector/dist/videojs-hls-quality-selector.js";

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
// Shared with the attendee materials player so the chosen speed follows the viewer
const PLAYBACK_RATE_STORAGE_KEY = 'ce_video_playback_rate';

// Глобальна змінна для downloadLink
let globalDownloadLink = null;

// Реєструємо компонент кнопки завантаження ОДИН РАЗ глобально
if (!videojs.getComponent('DownloadButton')) {
  const Button = videojs.getComponent('Button');

  class DownloadButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText('Download video');
    }

    buildCSSClass() {
      return `vjs-download-button ${super.buildCSSClass()}`;
    }

    createEl() {
      const el = super.createEl('button', {
        className: 'vjs-download-button vjs-control vjs-button'
      });

      // Створюємо іконку
      const icon = videojs.dom.createEl('span', {
        className: 'vjs-icon-placeholder',
        innerHTML: '<span aria-hidden="true">⬇</span>'
      });

      el.appendChild(icon);

      // Додаємо текст для accessibility
      const controlText = videojs.dom.createEl('span', {
        className: 'vjs-control-text',
        textContent: 'Download'
      });
      el.appendChild(controlText);

      return el;
    }

    handleClick() {
      if (globalDownloadLink) {
        const link = document.createElement('a');
        link.href = globalDownloadLink;
        link.download = 'video.mp4';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  videojs.registerComponent('DownloadButton', DownloadButton);
}

export default {
  name: 'VideoPlayer',

  props: {
    videoSrc: {
      type: String,
      required: true
    },
    downloadLink: {
      type: String,
      required: false,
      default: null
    },
    canDownload: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    trackingUrl: {
      type: String,
      required: false
    },
    csrfToken: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      player: null,
      playerId: `video-player-${Math.random().toString(36).substr(2, 9)}`,
      currentTime: 0,
      startTime: 0,
      endTime: 0,
      videoInterval: null,
      canUpdateViewedTime: false,
      isProcessing: false,
      hasLoadError: false,
      retryAttempts: 0,
      maxRetryAttempts: 3
    };
  },

  mounted() {
    if (this.videoSrc && this.isValidUrl(this.videoSrc)) {
      this.initPlayer();
    } else {
      this.hasLoadError = true;
    }
  },

  beforeDestroy() {
    this.destroyPlayer();
  },

  methods: {
    isValidUrl(url) {
      if (!url || typeof url !== 'string') return false;
      return /^https?:\/\//i.test(url);
    },

    initPlayer() {
      this.hasLoadError = false;
      this.isProcessing = true;

      const options = {
        controls: true,
        autoplay: this.autoplay,
        preload: 'auto',
        fluid: false,
        responsive: false,
        fill: true,
        html5: {
          vhs: {
            overrideNative: true
          },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false
        },
        playbackRates: PLAYBACK_RATES,
        controlBar: {
          pictureInPictureToggle: false
        }
      };

      // Ініціалізація Video.js
      this.player = videojs(this.$refs.videoPlayer, options);

      // Визначаємо тип контенту (HLS або MP4)
      const sourceType = this.videoSrc.includes('.m3u8') || this.videoSrc.includes('playlist')
        ? 'application/x-mpegURL'
        : 'video/mp4';

      // Встановлюємо джерело відео
      this.player.src({
        src: this.videoSrc,
        type: sourceType
      });

      // Додаємо Quality Selector для HLS
      if (sourceType === 'application/x-mpegURL' && this.player.hlsQualitySelector) {
        this.player.hlsQualitySelector({
          displayCurrentQuality: true,
        });
      }

      // Додаємо кнопку завантаження
      this.player.ready(() => {
        this.restorePlaybackRate();

        if (this.canDownload && this.downloadLink) {
          this.addDownloadButton();
        }

        // Блокування контекстного меню якщо завантаження заборонено
        if (!this.canDownload) {
          this.$refs.videoPlayer.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            return false;
          });
        }
      });

      // Підписуємось на події
      this.setupEventListeners();
    },

    addDownloadButton() {
      if (!this.player) return;

      // Оновлюємо глобальну змінну
      globalDownloadLink = this.downloadLink;

      // Додаємо кнопку перед fullscreenToggle якщо її ще немає
      if (!this.player.controlBar.getChild('DownloadButton')) {
        const fullscreenToggle = this.player.controlBar.getChild('fullscreenToggle');
        if (fullscreenToggle) {
          const index = this.player.controlBar.children().indexOf(fullscreenToggle);
          this.player.controlBar.addChild('DownloadButton', {}, index);
        } else {
          this.player.controlBar.addChild('DownloadButton', {});
        }
      }
    },

    restorePlaybackRate() {
      let rate = 1;
      try {
        rate = Number(window.localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY));
      } catch (e) {
        return;
      }

      if (!PLAYBACK_RATES.includes(rate) || rate === 1) return;

      // Default first: a media (re)load resets playbackRate to defaultPlaybackRate
      this.player.defaultPlaybackRate(rate);
      this.player.playbackRate(rate);
    },

    rememberPlaybackRate() {
      if (!this.player) return;

      const rate = this.player.playbackRate();
      if (!PLAYBACK_RATES.includes(rate)) return;

      // Keeps the speed across retryVideo() src() + load()
      this.player.defaultPlaybackRate(rate);

      try {
        window.localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, String(rate));
      } catch (e) {
        // Storage blocked (private mode) — speed still works for this video
      }
    },

    removeDownloadButton() {
      if (!this.player) return;

      const downloadBtn = this.player.controlBar.getChild('DownloadButton');
      if (downloadBtn) {
        this.player.controlBar.removeChild(downloadBtn);
      }
    },

    setupEventListeners() {
      if (!this.player) return;

      // Відео готове до відтворення
      this.player.on('loadedmetadata', () => {
        this.isProcessing = false;
        this.hasLoadError = false;
        this.retryAttempts = 0;
      });

      // Відео може починати відтворюватися
      this.player.on('canplay', () => {
        this.isProcessing = false;
      });

      // Відстеження часу
      this.player.on('timeupdate', () => {
        if (!this.player.seeking()) {
          this.currentTime = this.player.currentTime();
        }
      });

      // Початок відтворення
      this.player.on('play', () => {
        this.startTime = this.player.currentTime();

        if (this.trackingUrl) {
          this.videoInterval = setInterval(() => {
            this.sendPlayerAction('continue');
          }, 45000);
        }
      });

      this.player.on('ratechange', () => this.rememberPlaybackRate());

      // Відтворення почалось
      this.player.on('playing', () => {
        this.canUpdateViewedTime = true;
        this.isProcessing = false;
      });

      // Пауза
      this.player.on('pause', () => {
        this.endTime = this.currentTime < this.startTime
          ? this.player.currentTime()
          : this.currentTime;

        if (this.canUpdateViewedTime && this.trackingUrl) {
          this.sendPlayerAction('end');
          this.canUpdateViewedTime = false;
        }

        if (this.videoInterval) {
          clearInterval(this.videoInterval);
        }
      });

      // Завершення відео
      this.player.on('ended', () => {
        if (this.trackingUrl) {
          this.sendPlayerAction('end');
        }
        this.$emit('ended');
      });

      // Очікування даних
      this.player.on('waiting', () => {
        // Не показуємо processing якщо відео вже почало відтворюватися
        if (this.currentTime === 0) {
          this.isProcessing = true;
        }
      });

      // Помилка завантаження
      this.player.on('error', (error) => {
        console.error('Video player error:', error);
        this.isProcessing = false;
        this.hasLoadError = true;

        // Автоматична спроба перезавантаження
        if (this.retryAttempts < this.maxRetryAttempts) {
          setTimeout(() => {
            this.retryVideo();
          }, 3000);
        }

        this.$emit('error', error);
      });
    },

    retryVideo() {
      this.retryAttempts++;
      this.hasLoadError = false;
      this.isProcessing = true;

      if (this.player) {
        this.player.src({
          src: this.videoSrc,
          type: this.videoSrc.includes('.m3u8') || this.videoSrc.includes('playlist')
            ? 'application/x-mpegURL'
            : 'video/mp4'
        });
        this.player.load();
      } else {
        this.initPlayer();
      }
    },

    sendPlayerAction(type) {
      if (!this.trackingUrl) return;

      this.endTime = this.currentTime < this.startTime
        ? this.player.currentTime()
        : this.currentTime;

      const data = {
        _token: this.csrfToken,
        type: type || 'continue',
        start_time: this.startTime,
        end_time: this.endTime
      };

      if (this.startTime !== this.endTime) {
        sendData(this.trackingUrl, data)
          .then(() => {
            console.log('Tracking sent:', type);
          })
          .catch((error) => {
            console.error('Tracking error:', error);
          });
      }
    },

    destroyPlayer() {
      // Відправляємо останні дані перед знищенням
      if (this.player && !this.player.paused() && this.trackingUrl) {
        this.sendPlayerAction('end');
      }

      if (this.videoInterval) {
        clearInterval(this.videoInterval);
      }

      if (this.player) {
        this.player.dispose();
        this.player = null;
      }
    }
  },

  watch: {
    // Динамічна зміна можливості завантаження
    canDownload(newVal) {
      if (this.player) {
        if (newVal && this.downloadLink) {
          this.addDownloadButton();
        } else {
          this.removeDownloadButton();
        }
      }
    },

    downloadLink(newVal) {
      if (this.player && this.canDownload) {
        // Перестворюємо кнопку з новим посиланням
        this.removeDownloadButton();
        if (newVal) {
          this.addDownloadButton();
        }
      }
    }
  }
};
</script>

<style scoped>
.video-player-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-js {
  width: 100%;
  height: 100%;
}

.vjs-big-play-button {
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
}

.video-alert {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
}

.alert {
  max-width: 600px;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  color: #fff;
}

.alert i {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
}

.alert p {
  font-size: 18px;
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.alert-warning {
  background-color: #ff9800;
  border: 2px solid #f57c00;
}

.alert-info {
  background-color: #2196f3;
  border: 2px solid #1976d2;
}

.alert-error {
  background-color: #f44336;
  border: 2px solid #d32f2f;
}

.retry-button {
  background-color: #fff;
  color: #f44336;
  border: none;
  padding: 12px 30px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-button:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.retry-button:active {
  transform: translateY(0);
}
</style>

<style>
/* Глобальні стилі для кнопки завантаження (без scoped) */
.video-js .vjs-download-button {
  cursor: pointer !important;
}

.video-js .vjs-download-button .vjs-icon-placeholder {
  display: inline-block;
  font-size: 1.2em;
  line-height: 1.67;
}

.video-js .vjs-download-button .vjs-icon-placeholder span {
  font-size: 1.2em;
  display: block;
}

.video-js .vjs-download-button:hover .vjs-icon-placeholder {
  color: #fff;
}

.video-js .vjs-download-button .vjs-control-text {
  position: absolute;
  clip: rect(0 0 0 0);
  border: 0;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  width: 1px;
}
</style>